import React, { useCallback, useEffect } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    getPlayingTrackSeekPercent,
    getPlayingTrackStatus,
    getPlayingTrackUrl,
    setCurrentPlayingAudioDurationPercent,
} from 'js/src/data/media';
import type { NoOwnProps } from 'js/src/libs/consts';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';

type PropsFromRedux = ConnectedProps<typeof connector>;

const SimpleAudioTagPresentation = React.memo(function SimpleAudioTagPresentation({
    playingAudioUrl,
    playingAudioSeekPercent,
    isPlaying,
    setCurrentPlayingAudioDurationPercent,
}: PropsFromRedux & NoOwnProps) {
    // react refs
    const mp3SourceRef = React.useRef<HTMLSourceElement | null>(null);
    const audioNodeRef = React.useRef<HTMLAudioElement | null>(null);

    /**
     * effect to load the url.
     * This is useful for play/pause interaction and loading up the music asset to trigger the loadeddata event listner.
     */
    useEffect(() => {
        if (playingAudioUrl) {
            if (!audioNodeRef.current) throw new Error('audioNodeRef.current is null');
            if (!mp3SourceRef.current) throw new Error('mp3SourceRef.current is null');
            mp3SourceRef.current.src = playingAudioUrl;
            audioNodeRef.current.load();
        }
    });

    const play = useCallback(
        (e: any) => {
            const audioSeekTime = (
                (e.currentTarget.duration * playingAudioSeekPercent) /
                100
            ).toFixed(2);
            e.currentTarget.currentTime = audioSeekTime;
            e.currentTarget.play();
        },
        [playingAudioSeekPercent],
    );

    // Effect to play/pause audio.
    useEffect(() => {
        const currentAudioNode = audioNodeRef.current;
        if (isPlaying) {
            // set up the listener
            if (!currentAudioNode) throw new Error('audioNodeRef.current is null');
            currentAudioNode.addEventListener('loadeddata', play);
        } else {
            pause();
        }
        return () => {
            // remove the listener.
            if (currentAudioNode) {
                currentAudioNode.removeEventListener('loadeddata', play);
            }
        };
    }, [isPlaying, playingAudioSeekPercent, play]);

    const pause = () => {
        if (audioNodeRef.current) {
            audioNodeRef.current.pause();
        }
    };

    const onAudioDurationUpdate = (evt: React.SyntheticEvent<HTMLAudioElement>) => {
        if (evt.currentTarget.duration > 0) {
            setCurrentPlayingAudioDurationPercent(
                evt.currentTarget.currentTime,
                evt.currentTarget.duration,
            );
        }
    };

    return (
        <>
            {playingAudioUrl && (
                <div>
                    <audio ref={audioNodeRef} preload="none" onTimeUpdate={onAudioDurationUpdate}>
                        <source src={playingAudioUrl} ref={mp3SourceRef} />
                    </audio>
                </div>
            )}
        </>
    );
});

const mapStateToProps = (state: RootState) => {
    return {
        playingAudioUrl: getPlayingTrackUrl(state),
        playingAudioSeekPercent: getPlayingTrackSeekPercent(state),
        isPlaying: getPlayingTrackStatus(state),
    };
};
const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            setCurrentPlayingAudioDurationPercent,
        },
        dispatch,
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const SimpleAudioPlayer = connector(SimpleAudioTagPresentation);

export default SimpleAudioPlayer;
